import * as O from 'fp-ts/lib/Option';
import {AppBar, Tab, Tabs, useMediaQuery} from '@material-ui/core';
import {
  changeCommissionFilter,
  clearCommissions,
  EmptyCommissionFilter,
  getAdvancedCommissions,
  getEarnedCommissions,
  changeSort,
} from 'Agent/Commissions/ducks/commission';
import {Commission} from 'Agent/Commissions/models/Commission';
import {theme} from 'Core/components/WithTheme';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps} from 'react-router';
import {ReducerState} from 'rootReducer';
import FileDownloadButton, {FileExtension} from 'Shared/components/FileDownloadButton';
import FileDownloadFab from 'Shared/components/FileDownloadFab';
import Column from 'Shared/components/PagedView/Column';
import {
  withDateFilterProperties,
  withSelectFilterProperties,
  withStringFilterProperties,
} from 'Shared/components/PagedView/Filter';
import PagedView from 'Shared/components/PagedView/PagedView';
import {getSourceOptions} from 'Shared/constants/Source';
import {AgencyFilters} from 'Shared/models/AgencyFilters';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {toFormattedDate} from 'Shared/utilities/DateFormatter';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import './Commissions.sass';

type CommissionsProps = {
  selectedAgency: AgencyFilters;
} & RouteComponentProps;

enum CommissionTab {
  Earned = 0,
  Advanced = 1,
}

export const getCommissionDownloadFileName = (commission: Commission) =>
  `Commission-Statement-${toFormattedDate(commission.statementDate)}.pdf`;

export const Commissions = (props: CommissionsProps) => {
  const dispatch = useDispatch();
  const {selectedAgency} = props;
  const {agentNumbers, isNewYork, encryptedIdentifier} = selectedAgency;
  useBreadcrumbs('Commissions', props.match.url);
  const [currentTab, setCurrentTab] = useState(CommissionTab.Earned);
  const {commissions, sort, commissionsFilter: filter} = useSelector((state: ReducerState) => state.commissions);
  const sortAsUndefined = O.toUndefined(sort);

  useEffect(() => {
    return () => {
      dispatch(clearCommissions());
      dispatch(changeCommissionFilter(EmptyCommissionFilter));
    };
  }, []);

  const getCommissions = currentTab === CommissionTab.Earned ? getEarnedCommissions : getAdvancedCommissions;

  useEffect(() => {
    dispatch(getCommissions(agentNumbers, isNewYork, encryptedIdentifier, undefined, filter, sortAsUndefined));
  }, [currentTab, filter, sortAsUndefined, agentNumbers, isNewYork, encryptedIdentifier]);

  const changeFilter = withValueChangedHook(filter, changeCommissionFilter, dispatch);
  const filterProperties = {
    changeFilter,
    filterData: filter,
  };

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const exportToCsvProps = {
    startIcon: <i className="fa fa-2x fa-file-download" />,
    extension: FileExtension.CSV,
    downloadedFileName: currentTab === 0 ? 'FilteredEarnedCommissions' : 'FilteredAdvancedCommissions',
    relativeFileLocation: `/commissions/${
      currentTab === 0 ? 'earnedStatements' : 'advancedStatements'
    }${addFilterParameters({
      ...filter,
      agentNumbers: selectedAgency.agentNumbers,
      isNewYork: selectedAgency.isNewYork,
      encryptedIdentifier: selectedAgency.encryptedIdentifier,
      exportToCsv: true,
    })}`,
    children: 'Export To CSV',
  };

  const CommissionTabHeader = (
    <div className="commissions__tabs">
      <AppBar position="static">
        <Tabs
          value={currentTab}
          onChange={(event, newValue) => {
            setCurrentTab(newValue);
          }}
        >
          <Tab label="Earned Commissions" />
          <Tab label="Advanced Commissions" />
        </Tabs>
      </AppBar>
    </div>
  );

  return (
    <>
      {CommissionTabHeader}
      <PagedView
        pagedViewKey="commissions"
        loadMore={page =>
          dispatch(
            getCommissions(
              selectedAgency.agentNumbers,
              selectedAgency.isNewYork,
              selectedAgency.encryptedIdentifier,
              page,
              filter,
              sortAsUndefined
            )
          )
        }
        data={commissions}
        sort={O.toUndefined(sort)}
        onChangeSort={sort => dispatch(changeSort(sort))}
        actions={
          isMobile ? (
            <FileDownloadFab variant="extended" color="primary" displayTooltip {...exportToCsvProps} />
          ) : (
            <FileDownloadButton variant="contained" color="primary" displayTooltip {...exportToCsvProps} />
          )
        }
      >
        {columnProps => (
          <>
            <Column
              {...columnProps}
              title="Agent Name"
              filterProperties={withStringFilterProperties(filterProperties, 'agentName')}
              sortColumnKey="agentName"
              default
            >
              {commission => commission.agentName}
            </Column>
            <Column
              {...columnProps}
              title="Agent Number"
              filterProperties={withStringFilterProperties(filterProperties, 'agentNumber')}
              sortColumnKey="agentNumber"
              default
            >
              {commission => commission.agentNumber}
            </Column>
            <Column
              {...columnProps}
              title="Statement Date"
              filterProperties={withDateFilterProperties(filterProperties, 'statement')}
              sortColumnKey="statementDate"
              default
            >
              {commission => toFormattedDate(commission.statementDate)}
            </Column>
            <Column
              {...columnProps}
              title="Source"
              filterProperties={withSelectFilterProperties(
                filterProperties,
                'sourceId',
                getSourceOptions(selectedAgency.isNewYork)
              )}
              sortColumnKey="source"
              default
            >
              {commission => commission.source!.name}
            </Column>
            <Column {...columnProps} isActions title="Document" default>
              {commission => (
                commission.links[0] != null ?
                <div className="commissions__document-link">
                  {/* The API already added .pdf here, avoid double extensions */}
                  <FileDownloadButton
                    downloadedFileName={getCommissionDownloadFileName(commission)}
                    displayTooltip={false}
                    relativeFileLocation={
                      commission.links[0].href +
                      addFilterParameters({encryptedIdentifier: selectedAgency.encryptedIdentifier})
                    }
                  >
                    Download
                  </FileDownloadButton>                  
                </div>
                :
                <div>N/A</div>
              )}
            </Column>
          </>
        )}
      </PagedView>
    </>
  );
};

export default Commissions;
